import { rtLogService } from '@rammetec/core'
import type { Page, RestReadContext } from '@rammetec/data-access'
import { productDescriptor, type Product } from '@syscolect/domain'
import {
  filterAstToDataFilter,
  type DataColumnType,
} from './restScriptFilter'
import {
  extractMeta,
  extractRows,
  toProduct,
  type VhJobsResponse,
} from './restScriptVectorhub'

/** Maps productDescriptor field types → VH DataColumnType (memoized at module load). */
function productFieldTypeLookup(): (field: string) => DataColumnType | undefined {
  const map = new Map<string, DataColumnType>()
  for (const field of productDescriptor.fields) {
    const t = field.type
    if (t === 'number') map.set(field.name, 'number')
    else if (t === 'boolean') map.set(field.name, 'boolean')
    else if (t === 'date') map.set(field.name, 'date')
    else map.set(field.name, 'string')
  }
  return (field) => map.get(field)
}

const fieldType = productFieldTypeLookup()

/**
 * RestScript `getAll` handler for Product — forwards pagination + flattened
 * `filterAst` to VectorHub `data.execute` and maps `job.result.meta` into a
 * {@link Page} envelope.
 */
export async function productRestScriptGetAll(
  ctx: RestReadContext,
): Promise<Product[] | Page<Product>> {
  const orgId = ctx.fixedWhere.org
  if (!orgId) {
    throw new Error(
      '[restScriptProductHandler] RestScript getAll requires org scope (fixedWhere.org).',
    )
  }

  if (ctx.tableName !== 'Product') return []

  const queryId = `data-${ctx.tableName.toLowerCase()}`
  const { limit, offset, countTotal, filterAst, sort } = ctx.options ?? {}
  const page = limit != null ? { limit, offset: offset ?? 0 } : undefined
  const filter = filterAstToDataFilter(filterAst, fieldType)

  const res = await ctx.request<VhJobsResponse>('POST', '/jobs', {
    orgId,
    action: 'data.execute',
    params: {
      queryId,
      ...(page ? { page } : {}),
      ...(countTotal ? { countTotal: true } : {}),
      ...(filter ? { filter } : {}),
      // ISort ≡ DataSort (VH 0035) — forward as-is; VH validates against its whitelist.
      ...(sort ? { sort } : {}),
    },
    sync: true,
    syncTimeout: 30_000,
  })

  const items = extractRows(res.job).map(toProduct)
  const meta = extractMeta(res.job)
  if (meta.ignoredFilters?.length) {
    rtLogService.warn('[restScriptProductHandler] filtros ignorados pelo VH', {
      queryId,
      ignoredFilters: meta.ignoredFilters,
    })
  }
  if (meta.ignoredSort) {
    rtLogService.warn('[restScriptProductHandler] ordenação ignorada pelo VH', {
      queryId,
      ignoredSort: meta.ignoredSort,
    })
  }

  if (!page && !countTotal) return items

  return {
    items,
    hasMore: meta.hasMore ?? false,
    total: meta.totalCount,
    offset: meta.offset ?? page?.offset ?? 0,
  }
}
